import { GeneralAPI } from '../apis/GeneralAPI'
import Header from '../components/Header'
import LandingPageWrapper from '../components/HomePage/LandingPageWrapper'
import BookingSection from '../components/PestLanding/BookingSection'
import CampaignStrategy from '../components/PestLanding/CampaignStrategy'
import FAQ from '../components/PestLanding/FAQ'
import HowItWorks from '../components/PestLanding/HowItWorks'
import PestHero from '../components/PestLanding/PestHero'
import SocialProof from '../components/PestLanding/SocialProof'
import WhyOLM from '../components/PestLanding/WhyOLM'

const MORTGAGE_FALLBACK_META = `
<meta name="description" content="Reach homeowners, homebuyers, real estate partners, and high-intent lending prospects with targeted mortgage direct mail campaigns." />
<meta property="og:title" content="Mortgage Direct Mail Marketing That Works" />
<meta property="og:description" content="Reach homeowners, homebuyers, real estate partners, and high-intent lending prospects with targeted mortgage direct mail campaigns." />
`

const HERO_BULLETS = [
    'Target by geography, property profile, home equity, loan age, household signals, and real estate activity',
    'Build campaigns for purchase loans, refinance opportunities, HELOCs, reverse mortgages, recruiting, and referral partners',
    'Fast in-house production: mail drops in days, not weeks',
    'Track response with call tracking, QR codes, and campaign-level reporting',
]

const CAMPAIGNS = [
    {
        title: 'Purchase prospects',
        description:
            'Reach likely movers, growing households, renters, and other audiences preparing for a home purchase.',
        iconBg: 'bg-secondary/10',
        iconColor: 'text-secondary',
        icon: 'bolt',
    },
    {
        title: 'Refinance opportunities',
        description:
            'Target homeowners by loan age, equity, property profile, and market conditions when a refinance may make sense.',
        iconBg: 'bg-[#FAB331]/20',
        iconColor: 'text-secondary',
        icon: 'home',
    },
    {
        title: 'Realtor farming',
        description:
            'Build familiarity with agents and real estate offices in the ZIP codes and communities you want to grow.',
        iconBg: 'bg-[#DBEAFE]',
        iconColor: 'text-[#1E40AF]',
        icon: 'map-pin',
    },
    {
        title: 'Past-client nurture',
        description:
            'Stay in touch after closing for future moves, refinancing, home equity needs, and referrals.',
        iconBg: 'bg-[#DCFCE7]',
        iconColor: 'text-[#166534]',
        icon: 'refresh',
    },
    {
        title: 'Home equity offers',
        description:
            'Reach equity-rich homeowners with relevant HELOC, cash-out, renovation, or debt-consolidation messages.',
        iconBg: 'bg-secondary/10',
        iconColor: 'text-secondary',
        icon: 'wrench',
    },
    {
        title: 'Referral partner outreach',
        description:
            'Create memorable campaigns for real estate agents, builders, financial advisors, and other referral sources.',
        iconBg: 'bg-gray-200',
        iconColor: 'text-gray-600',
        icon: 'building-office',
    },
]

const COMPARISONS = [
    {
        bad: {
            title: 'Basic radius lists',
            desc: 'Broad ZIPs or carrier routes with little strategy',
        },
        good: {
            title: 'Smarter borrower and partner targeting',
            desc: '',
        },
    },
    {
        bad: {
            title: 'Slow campaign launches',
            desc: 'Missed timing when rates, inventory, or demand shifts',
        },
        good: {
            title: 'Fast production options',
            desc: 'In-house print and mail with 2-business-day production available',
        },
    },
    {
        bad: {
            title: 'Generic creative',
            desc: 'Same postcard message regardless of audience or job type',
        },
        good: {
            title: 'Mortgage-specific campaigns',
            desc: '',
        },
    },
    {
        bad: {
            title: 'No response visibility',
            desc: 'Hard to know which campaign created calls',
        },
        good: {
            title: 'Trackable performance',
            desc: 'Call tracking, QR codes, and campaign-level reporting',
        },
    },
    {
        bad: {
            title: 'One-off postcard orders',
            desc: 'No cadence, testing plan, or next step',
        },
        good: {
            title: 'Repeatable growth program',
            desc: 'Audience, offer, format, cadence, and follow-up aligned to your market',
        },
    },
]

const STEPS = [
    {
        num: 1,
        color: '#ED5C2F',
        title: 'Strategy call',
        desc: '20 minutes. We review your market, loan mix, target borrowers, referral channels, campaign history, budget, and growth goals.',
    },
    {
        num: 2,
        color: '#F28230',
        title: 'Audience + offer',
        desc: 'We define which borrowers or partners to mail, where to focus, what message to lead with, and which format fits the campaign goal.',
    },
    {
        num: 3,
        color: '#FAB331',
        title: 'Produce + mail',
        desc: 'Our in-house print and mail operation keeps campaigns moving quickly so you can respond to market shifts, seasonal opportunities, and partner initiatives.',
    },
    {
        num: 4,
        color: '#3F454F',
        title: 'Track + improve',
        desc: 'Use call tracking, QR codes, landing pages, and response data to compare audiences, offers, and creative, then improve the next drop.',
    },
]

const FAQS = [
    {
        question: 'How is this different from EDDM (every door direct mail)?',
        answer: 'EDDM mails every address on a carrier route. That can support broad awareness, but it does not distinguish likely borrowers, homeowners with relevant equity, or referral partners. We can target campaigns using geography, property and household data, loan-related signals, and real estate activity.',
    },
    {
        question: 'What mortgage campaigns work well with direct mail?',
        answer: 'Common use cases include purchase prospecting, refinance and home-equity outreach, reverse mortgage education, past-client nurture, realtor and builder partnerships, and recruiting loan officers. The right campaign depends on your market, products, audience, and compliance requirements.',
    },
    {
        question: 'How fast can you get mail out?',
        answer: 'We offer 2-business-day production for most formats once creative and lists are approved. Standard turnaround is 5-7 business days. Time-sensitive market, partner, and seasonal campaigns can move faster when timing matters.',
    },
    {
        question: "What's the minimum spend to get started?",
        answer: "There's no fixed minimum, but this is built for intentional campaigns rather than one-off print orders. Program size depends on your market, audience, mail volume, and growth goals. If you only need the cheapest postcard order, we're probably not the right fit.",
    },
]

export default function Mortgage({ data }) {
    return (
        <LandingPageWrapper sectionSpacing={false} paddingFromTop={false}>
            <Header
                meta={data?.yoast_head || MORTGAGE_FALLBACK_META}
                metaTitle="Mortgage Direct Mail Marketing That Works"
            />
            <PestHero
                eyebrow="For Mortgage Brokers"
                heading={['More funded loans.', 'Less wasted mail.']}
                description="Reach homeowners, homebuyers, real estate partners, and high-intent lending prospects with direct mail targeted by market, property, life stage, and loan opportunity."
                cta="Plan your mortgage campaign"
                fitText="Best fit for growth-minded mortgage brokers, lenders, and loan teams that want a more consistent pipeline beyond purchased leads and digital ads."
                bullets={HERO_BULLETS}
                image={{
                    src: '/assets/homepage/mortgage-hero.png',
                    alt: 'Mortgage direct mail campaign examples for homebuyer and refinance lead generation',
                    width: 1124,
                    height: 992,
                }}
            />
            <SocialProof eyebrow="Trusted by growth-minded businesses across the U.S." />
            <CampaignStrategy
                eyebrow="Mortgage direct mail strategy"
                title="Direct mail built around the lending opportunities you actually want more of."
                description="Stay visible with the borrowers and referral partners most likely to need financing - using relevant offers, smarter audience data, and campaigns designed to build trust before the next loan conversation."
                campaigns={CAMPAIGNS}
            />
            <WhyOLM
                title="Built for mortgage brokers who need more than another source of expensive leads."
                paragraphs={[
                    'Mortgage decisions are high-trust, locally influenced, and often won by the professional prospects remember before they start comparing rates. Direct mail helps you show up earlier and stay recognizable.',
                    'From purchase and refinance prospects to past clients and real estate partners, OLM turns direct mail into a focused pipeline channel - not another generic postcard drop sent to everyone.',
                ]}
                comparisons={COMPARISONS}
            />
            <HowItWorks
                title="Launch a mortgage campaign"
                gradientTitle="without guessing who to mail next."
                description="We start with your market, loan products, ideal borrower, referral strategy, current marketing, and growth goals. Then we build the audience, offer, creative, production plan, and tracking around the lending opportunities you want to win."
                steps={STEPS}
            />
            <FAQ title="Questions mortgage brokers ask first." faqs={FAQS} />
            <BookingSection
                titleHighlight="mortgage strategy call."
                description="Tell us a bit about your business and grab a time that works for you. Takes about a minute."
                containerId="mortgage-landing-page"
                classPrefix="mortgage"
            />

            <style
                dangerouslySetInnerHTML={{
                    __html: `
                @media (max-width: 640px) {
                    .mortgage-form-grid {
                        grid-template-columns: 1fr !important;
                        padding: 20px !important;
                        gap: 16px !important;
                    }
                    .mortgage-form-footer {
                        justify-content: center !important;
                        text-align: center;
                    }
                    .mortgage-form-footer button {
                        width: 100%;
                    }
                }
            `,
                }}
            />
        </LandingPageWrapper>
    )
}

export async function getStaticProps() {
    let data = {}

    try {
        const page = await GeneralAPI.getPageBySlug('/mortgage')
        data = page ? page[0] || {} : {}
    } catch (e) {
        console.error(e)
    }

    try {
        const { banners } = await GeneralAPI.getACFBanners()
        data.banners = banners
    } catch (e) {
        console.error(e)
    }

    return {
        props: {
            data,
            hideCartButton: true,
            hideLoginButton: true,
            phoneNumber: '978-893-5890',
        },
        revalidate: 10,
    }
}
